import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogTitle, DialogContent } from "@mui/material";
import styles from "./TwoFAEditModal.module.css";
import { useTranslation } from "react-i18next";

export default function TwoFAEditModal({ open, onClose, onConfirm, onResend }) {
  const { t } = useTranslation("transfersTableModal");
  const [code, setCode] = useState(Array(6).fill(""));
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [error, setError] = useState("");
  const [inputError, setInputError] = useState("");

  const validateCode = () => {
    if (code.includes("")) {
      setError("El código está incompleto");
      return false;
    }
    setError("");
    return true;
  };

  useEffect(() => {
    if (open) {
      setCode(Array(6).fill(""));
      setError("");
      setInputError("");
      setFocusedIndex(null);

      setTimeout(() => {
        const first = document.querySelector("input[name='0']");
        if (first) first.focus();
      }, 50);
    }
  }, [open]);

  const handleInput = (index, value) => {
    if (!/^[0-9]?$/.test(value)) {
      setInputError("Solo se permiten números");
      return false;
    }

    setInputError("");

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    return true;
  };

  const handleBackspace = (index) => {
    const newCode = [...code];
    newCode[index] = "";
    setCode(newCode);

    if (index > 0) {
      const prev = document.querySelector(`input[name='${index - 1}']`);
      if (prev) prev.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    if (paste.length === 6 && /^[0-9]{6}$/.test(paste)) {
      const values = paste.split("");
      setCode(values);
      setError("");
      setInputError("");
      
      setTimeout(() => {
        const last = document.querySelector("input[name='5']");
        if (last) last.focus();
      }, 0);
    }
  };

  const handleSubmit = () => {
    if (!validateCode()) return;
    onConfirm(code.join(""));
  };

  const handleCancel = () => {
    setCode(Array(6).fill(""));
    setError("");
    setInputError("");
    onClose(); // cerrar modal
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className={styles.title}>{t("textTitle")}</DialogTitle>
      <DialogContent>
        <div className={styles.description}>{t("textDescription")}</div>

        <div className={styles.codeContainer}>
          {code.map((value, index) => (
            <React.Fragment key={index}>
              <input
                type="text"
                maxLength="1"
                value={value}
                id={`twofa_code_input_${index}`}
                name={index}
                autoFocus={index === 0}
                className={`${styles.codeInput} ${error ? styles.error : ""} ${
                  focusedIndex !== null && focusedIndex !== index
                    ? styles.blur
                    : ""
                }`}
                onChange={(e) => {
                  const val = e.target.value;

                  const ok = handleInput(index, val);
                  if (!ok) return;

                  if (/^[0-9]$/.test(val)) {
                    const next = document.querySelector(
                      `input[name='${index + 1}']`,
                    );
                    if (next) next.focus();
                  }
                }}
                onPaste={handlePaste}
                onKeyUp={(e) => {
                  if (e.key === "Backspace") handleBackspace(index);
                  if (e.key === "Enter") handleSubmit();
                }}
                onFocus={() => setFocusedIndex(index)}
                onBlur={() => setFocusedIndex(null)}
              />

              {index === 2 && <div className={styles.separator}></div>}
            </React.Fragment>
          ))}
        </div>

        {inputError && <div className={styles.errorText}>*{inputError}</div>}

        {error && <div className={styles.errorText}>*{error}</div>}

        <div className={styles.noCodeContainer}>
          <span>{t("buttonReenviarText")}</span>
          <span onClick={onResend} className={styles.resendLink}>
            {t("buttonReenviar")}
          </span>
        </div>

        <div className={styles.buttons}>
          <Button
            variant="contained"
            fullWidth
            id="twofa_modal_confirm_button"
            onClick={handleSubmit}
            className={styles.confirmButton}>
            {t("buttonConfirm")}
          </Button>

          <Button
            variant="text"
            fullWidth
            id="twofa_modal_cancel_button"
            onClick={handleCancel}
            className={styles.cancelButton}>
            {t("buttonCancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
